// src/hooks/dashboard/useDashboard.ts
import { useEffect, useMemo, useState } from "react";
import type { Layout, Layouts } from "react-grid-layout";
import type { Breakpoint, DashboardWidget } from "../../types/dashboard/dashboard.types";
import {
  applyCurrentLayoutToWidgets,
  buildLayoutsFromWidgets,
  getDefaultWidth,
  normalizeWidgets,
} from "../../services/dashboard/dashboard.mapper";

// ✅ on garde TES imports (à adapter au chemin réel de ton api.ts)
import {
  fetchDashboardWidgets,
  addWidget,
  updateDashboard,
  deleteWidget,
} from "../../components/Dashboard/api";

export function useDashboard(id_dashboard: number) {
  const [layouts, setLayouts] = useState<Layouts>({});
  const [widgets, setWidgets] = useState<DashboardWidget[]>([]);
  const [currentBreakpoint, setCurrentBreakpoint] = useState<Breakpoint>("lg");
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"saving" | "success" | "error" | "">("");

  const canSave = useMemo(() => widgets.length > 0, [widgets.length]);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      try {
        const raw = await fetchDashboardWidgets(id_dashboard);
        const normalized = normalizeWidgets(raw);
        const builtLayouts = buildLayoutsFromWidgets(normalized);

        if (!mounted) return;
        setWidgets(normalized);
        setLayouts(builtLayouts);
      } catch (e) {
        if (!mounted) return;
        setWidgets([]);
        setLayouts({});
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [id_dashboard]);

const onLayoutChange = (currentLayout: Layout[], allLayouts: Layouts) => {
  setLayouts(allLayouts);

  // ✅ Layout de référence = lg
  const baseLayout = allLayouts?.lg ?? currentLayout;

  setWidgets((prev) =>
    prev.map((w) => {
      const l = baseLayout.find((it) => it.i === w.id);
      if (!l) return w;

      return {
        ...w,
        x: l.x,
        y: l.y,
        w: l.w,
        h: l.h,
      };
    })
  );
};

const save = async () => {
  setSaveStatus("saving");
  try {
    const payload = widgets.map((w) => ({
      id_widget: w.id_widget,
      content: w.content ?? "",
      type: w.type ?? "",
      x: Number(w.x ?? 0),
      y: Number(w.y ?? 0),
      w: Number(w.w ?? 1),
      h: Number(w.h ?? 2),
    }));

    console.log("SAVE PAYLOAD", payload);

    await updateDashboard(payload as any); // <= si updateDashboard attend DashboardWidget[], on peut adapter aussi
    setSaveStatus("success");
  } catch (e) {
    setSaveStatus("error");
  }
};

  const addNewWidget = async (content: string, type: string) => {
    // même payload que ton handleAddWidget :contentReference[oaicite:17]{index=17}
    const widgetData: Omit<DashboardWidget, "id"> = {
      id_dashboard,
      id_widget: 0,
      id_user: 1, // TODO: remplacer par user connecté
      content,
      type,
      x: 0,
      y: 0,
      w: getDefaultWidth("lg"),
      h: 2,
    };

    const added = await addWidget(widgetData as any);
    const id_widget = added?.id_widget;
    const id = String(id_widget ?? "0");

    const newWidget: DashboardWidget = {
      id,
      ...widgetData,
      id_widget: Number(id_widget ?? 0),
    };

    setWidgets((prev) => [...prev, newWidget]);
    setLayouts((prev) => {
      const copy: Layouts = { ...prev };
      Object.keys(copy).forEach((bp) => {
        const list = copy[bp] ?? [];
        copy[bp] = [
          ...list,
          { i: id, x: 0, y: 0, w: getDefaultWidth(bp as Breakpoint), h: 2 },
        ];
      });
      return copy;
    });
  };




// ✅ SUPPRIMER (avec id_widget)
const removeWidget = async (id_widget: string | number) => {
  const removed = widgets.find((w) => w.id_widget === Number(id_widget));
  const removedGridId = removed?.id;

  // ✅ FIX TS2345 ICI
  await deleteWidget(String(id_widget));

  setWidgets((prev) =>
    prev.filter((w) => w.id_widget !== Number(id_widget))
  );

  if (!removedGridId) return;

  setLayouts((prev) => {
    const copy: Layouts = { ...prev };
    Object.keys(copy).forEach((bp) => {
      copy[bp] = (copy[bp] ?? []).filter((l) => l.i !== removedGridId);
    });
    return copy;
  });
};




  return {
    widgets,
    layouts,
    loading,
    saveStatus,
    canSave,
    currentBreakpoint,
    setCurrentBreakpoint,
    onLayoutChange,
    addNewWidget,
    removeWidget,
    save,
  };
}
